import * as moment from 'moment';
import { CSSProperties, useState } from 'react';
import { DonationType, MatchStatus } from '@nkr/enums/index';
import { IDonor, IPosition } from '@nkr/models/index';
import { Alert, Button, Overlay, Popover } from 'react-bootstrap';
import useSwapQuery, { ISwapQuery } from '@nkr/store/swaps/hooks/use-swap-query';
import ReactLoading from 'react-loading';
import AlleleMatch from '@nkr/components/allele-match';

const BridgeIcon = (props) => (
    <svg
        xmlns='http://www.w3.org/2000/svg'
        xmlSpace='preserve'
        style={{
            enableBackground: 'new 0 0 122.88 51.73',
        }}
        viewBox='0 -35.58 122.88 122.88'
        {...props}
    >
        <path
            d='M16.55 3.48c.57-4.79 7.86-4.47 7.86 0v1.01c5.29 10.8 15.2 16.77 26.06 19 5.66 1.17 11.59 1.32 17.27.63 5.67-.69 11.08-2.23 15.7-4.46 7.2-3.47 12.5-8.59 13.91-14.73V3.48c.57-4.79 7.86-4.47 7.86 0v2.4c2.18 4.3 5.17 7.74 8.52 10.51 2.87 2.37 6.01 4.28 9.15 5.83v2.98c-1.16-.55-2.33-1.13-3.48-1.77v9.1h3.48v4.59h-17.67v14.62h-7.86V37.11H24.41v14.62h-7.86V37.11H0v-4.59h2.91v-9.4c-.96.55-1.94 1.06-2.91 1.55v-3.01c2.75-1.45 5.47-3.17 7.97-5.26 3.39-2.84 6.39-6.36 8.58-10.79V3.48zm50.18 23.45v5.59h-2.69v-5.38c-2.04.11-4.09.11-6.15 0v5.38H55.2v-5.6c-1.77-.18-3.53-.45-5.27-.81-.32-.07-.64-.13-.95-.21v6.61h-2.69v-7.3c-2.11-.6-4.16-1.35-6.15-2.25v9.55h-2.69V21.64c-2.2-1.19-4.29-2.59-6.22-4.2v15.08h-2.69V14.96c-1.51-1.54-2.9-3.24-4.14-5.1v22.67h72.95V11.58c-1.08 1.66-2.41 3.21-3.96 4.65v16.29H90.7V18.44a38.727 38.727 0 0 1-6.09 3.63l-.13.06v10.39h-2.69v-9.2c-1.96.79-4.02 1.47-6.15 2.03v7.18h-2.69v-6.54c-1.6.34-3.24.61-4.89.81-.44.04-.88.09-1.33.13zm45.15-8.59v14.19h4.83V21.84a44.15 44.15 0 0 1-4.69-3.39l-.14-.11zm-2.69 14.19V15.87a35.785 35.785 0 0 1-3.98-4.78v21.43h3.98v.01zM13.11 15.21v17.32h3.44V10.91a34.848 34.848 0 0 1-3.44 4.3zm-2.69 17.32V17.82c-.24.21-.49.43-.74.63a43.48 43.48 0 0 1-4.08 3.03v11.05h4.82z'
            style={{
                fillRule: 'evenodd',
                clipRule: 'evenodd',
            }}
            stroke={props.stroke ? props.stroke : 'black'}
            strokeWidth={props.strokeWidth ? props.strokeWidth : '2'}
        />
    </svg>
);

const LockIcon = (props) => (
    <svg
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        {...props}
    >
        <g id="SVGRepo_bgCarrier" strokeWidth={0} />
        <g
            id="SVGRepo_tracerCarrier"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <g id="SVGRepo_iconCarrier">
            <path
                d="M12 14.5V16.5M7 10.0288C7.47142 10 8.05259 10 8.8 10H15.2C15.9474 10 16.5286 10 17 10.0288M7 10.0288C6.41168 10.0647 5.99429 10.1455 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C18.0057 10.1455 17.5883 10.0647 17 10.0288M7 10.0288V8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V10.0288"
                stroke={props.stroke ? props.stroke : 'black'}
                strokeWidth={props.strokeWidth ? props.strokeWidth : '2'}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </g>
    </svg>
);

type TProps = {
    donor: IDonor
}

const LockingMatchPopover = ({ donor }: TProps) => {
    const [target, setTarget] = useState(null);
    const [query, setQuery] = useState<ISwapQuery>(null);
    const [show, setShow] = useState<boolean>(false);
    const { list, loading, error } = useSwapQuery(query)

    const getIconParameters = () => {
        let description = '';
        let icon: JSX.Element = null;

        if (donor.donationTypeId == DonationType.Bridge) {
            icon = <span style={{ color: 'black' }} title='This is a bridge donor' onClick={(e) => handleShow(e.target)}><BridgeIcon width='22px' height='22px' strokeWidth='1' /></span>;
            description = 'This is a bridge donor';
        } else if (donor.statusId > MatchStatus.MatchOffered) {
            icon = <span title='This donor is in a match that has already been commenced' onClick={(e) => handleShow(e.target)}><LockIcon width='18px' height='18px' fill='red' /></span>;
            description = 'This donor is in a match that has already been commenced';
        } else if (donor.statusId == MatchStatus.MatchOffered) {
            icon = <span title='This donor is in a pending match offer in the Offers tab' onClick={(e) => handleShow(e.target)}><LockIcon width='18px' height='18px' fill='orange' /></span>;
            description = 'This donor is in a pending match offer in the Offers tab';
        }

        return { description, icon };
    };

    const positions = list.reduce<IPosition[]>((acc, v) => {
        const p = v.positions.find(f => f.donor.id == donor.id);

        if (p) {
            acc.push(p);
        }

        return acc;
    }, []);

    const handleShow = (target) => {
        setQuery({ donors: [donor.id] });
        setTarget(target);
        setShow(!show);
    }

    const headerStyle: CSSProperties = {
        verticalAlign: 'bottom',
        backgroundColor: '#2d5f3f',
        color: 'white',
        padding: '2px',
        fontWeight: 'normal'
    }

    const rows = positions.map((m: IPosition) => {
        const kp = m.kidneyPickup ? moment(m.kidneyPickup).format('MM/DD/YYYY') : '-';
        const ka = m.kidneyArrival ? moment(m.kidneyArrival).format('MM/DD/YYYY') : '-';

        return (<tr className='text-center'>
            <td>{m.positionNumber}</td>
            <td>{m.donor.center.alias}</td>
            <td>{m.donor.alias}</td>
            <td>{m.donor.bloodType}</td>
            <td>{m.donor.age}</td>
            <td>{m.recipient.center.alias}</td>
            <td>{m.recipient.alias}</td>
            <td>{m.recipient.bloodType}</td>
            <td>{m.recipient.age}</td>
            <td>{m.cPRA4}</td>
            <td>{m.ewt}</td>
            <td>{m.wt}</td>
            <td>{m.donorPreselect?.donorPreselectDecision ?? 'Pending'}</td>
            <td>{m.singleEpletMmRisk?.label ?? '-?-'}</td>
            <td><AlleleMatch bmtMm={m.bmtMm} displayAsInline={false} /></td>
            <td>{kp}</td>
            <td>{ka}</td>
        </tr>);
    });

    const { description, icon } = getIconParameters();

    if (description == '') {
        return null;
    }

    return (
        <>
            <span style={{ cursor: 'pointer', paddingLeft: 5 }}>{icon}</span>
            <Overlay show={show} placement='bottom' target={target} container={document.body}>
                <Popover id='eplet-overlay' style={{ maxWidth: 1200, minWidth: 1200, maxHeight: 210 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', height: 28 }}>
                        <div style={{ fontWeight: 'bold' }}>{description}</div>
                        <div style={{ textAlign: 'right' }}><span style={{ cursor: 'pointer' }} className={'glyphicon glyphicon-remove'} aria-hidden='true' onClick={() => setShow(false)} /></div>
                    </div>
                    {!loading && !error && <div style={{ overflowY: 'auto', height: '105px' }}>
                        <table className='table table-bordered table-condensed'>
                            <thead>
                                {/* First header row - spanning columns */}
                                <tr>
                                    <th rowSpan={2} className='text-center' style={headerStyle}>
                                        Position
                                </th>
                                    <th colSpan={4} className='text-center' style={headerStyle}>
                                        Donor
                                </th>
                                    <th colSpan={7} className='text-center' style={headerStyle}>
                                        Recipient
                                </th>
                                    <th rowSpan={2} className='text-center' style={headerStyle}>
                                        Preselect Status
                                </th>
                                    <th rowSpan={2} className='text-center' style={headerStyle}>
                                        Eplet MM
                                </th>
                                    <th rowSpan={2} className='text-center' style={headerStyle}>
                                        Allele Match
                                </th>
                                    <th colSpan={2} className='text-center' style={headerStyle}>
                                        Target Surgery Dates
                                </th>
                                </tr>

                                {/* Second header row - sub-columns */}
                                <tr>
                                    <th className='text-center' style={headerStyle}>Center</th>
                                    <th className='text-center' style={headerStyle}>Alias</th>
                                    <th className='text-center' style={headerStyle}>ABO</th>
                                    <th className='text-center' style={headerStyle}>Age</th>
                                    <th className='text-center' style={headerStyle}>Center</th>
                                    <th className='text-center' style={headerStyle}>Alias</th>
                                    <th className='text-center' style={headerStyle}>ABO</th>
                                    <th className='text-center' style={headerStyle}>Age</th>
                                    <th className='text-center' style={headerStyle}>cPRA4</th>
                                    <th className='text-center' style={headerStyle}>EWT</th>
                                    <th className='text-center' style={headerStyle}>WT</th>
                                    <th className='text-center' style={headerStyle}>Donor</th>
                                    <th className='text-center' style={headerStyle}>Recipient</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows}
                            </tbody>
                        </table>
                    </div>}
                    {error && <Alert bsStyle='danger'>An error occurred while retrieving swap data for this donor.</Alert>}
                    {loading &&
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10px' }}>
                            <ReactLoading type={'spin'} color={'black'} />
                        </div>
                    }
                    <div style={{ flex: '1', display: 'flex', justifyContent: 'center' }}>
                        <Button bsSize='sm' onClick={() => setShow(false)}>Dismiss</Button>
                    </div>
                </Popover>
            </Overlay>
        </>
    );
}

export default LockingMatchPopover;
